import { AppContext } from "Context/AppContext";
import { AttributeContext } from "../AttributeContext";
import AppSettings from "Settings/AppSettings";
import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "Components/DesignSystem/Toast/useToast"
import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import LoadingSuccessErrorAlert from "./LoadingSuccessErrorAlert";
import CategoriesView from "./CategoriesView";
import AttributeValuesView from "./AttributeValuesView";
import ABSView from "./ABSView";
import AttributeConstraintView from "./AttributeConstraintView";
import CustomAttributeTag from "Components/Common/CustomAttributeTag";
import { TAttribute, TAttributeDetail } from "../types";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import { ABS_SELECTION } from "../AttributeConstants";
import Card from "Components/DesignSystem/Card/Card";
import Typography from "Components/DesignSystem/Typography/Typography";
import Button from "Components/DesignSystem/Button/Button";
import Menu from "Components/DesignSystem/Menu/Menu";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import { useDismissable } from "Components/DesignSystem/useDismissable";
import { Copy, MoreVertical } from "Components/DesignSystem/Iconography/Icons";
import { FOCUS_RING } from "Components/DesignSystem/focusRing";

type MyProps = {
  setAttrListApiCompleted: any
}

const AttributeDetails = ({ setAttrListApiCompleted }: MyProps) => {
  let api_base_url = AppSettings.GMS_BACKEND_API_URL;
  let appData = useContext(AppContext);
  let industryId =
    appData.get_selected_industry() && appData.get_selected_industry()?.id;
  const {
    editingItem,
    setEditingItem,
    setListReloadCounter,
    setDeleteInfo,
    setNewAttributeId,
    setAttributeDeleted,
    setDetailLoading,
  } = useContext(AttributeContext) as any;

  const [showLoader, setShowLoader] = React.useState(false);

  let [loading, setLoading] = React.useState<{
    status?: string,
    message?: string
  }>({
    status: "",
    message: "",
  });
  let [AttributeDetail, setAttributeDetail] = React.useState<TAttributeDetail | null>(null);
  const [clipText, setClipText] = useState<string | null>(null);

  /* The kebab (was a Bootstrap dropdown). DS `Menu` is presentational — this
     pair owns whether it is mounted, and `useDismissable` supplies
     Escape/outside-click, which the plugin used to. */
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);
  useDismissable({
    isOpen: kebabOpen,
    onDismiss: () => setKebabOpen(false),
    isInside: (target: Node) => !!kebabRef.current?.contains(target),
  });

  /* Final phase (was BootBox): the delete confirm and the delete-failure notice
     are DS Dialogs now, state-driven — the category Phase-6 pattern. */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<string | null>(null);

  /* "UID Copied" dismisses itself after 2s (as before), but through a ref so a
     re-click within the window restarts the full 2s instead of being cut short
     by the first click's timer, and unmount clears it. */
  const clipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (clipTimerRef.current) clearTimeout(clipTimerRef.current);
  }, []);

  function handle_clipbord(text?: string) {
    if (!text) return
    navigator.clipboard.writeText(text).then(() => setClipText(text));
    if (clipTimerRef.current) clearTimeout(clipTimerRef.current);
    clipTimerRef.current = setTimeout(() => {
      setClipText(null);
    }, 2000);
  }

  function handle_url_clipbord(id?: string) {
    if (!id) return
    const url = `${window.location.origin}${window.location.pathname}`;
    const categoryID = `/${id}`;
    if (url.includes(id)) {
      navigator.clipboard
        .writeText(`${url}`)
        .then(() => toast.success("Url Copied to clipboard"));
    } else {
      navigator.clipboard
        .writeText(`${url}${categoryID}`)
        .then(() => toast.success("Url Copied to clipboard"));
    }
  }

  useEffect(() => {
    load_Attribute_detail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem.id]);

  async function load_Attribute_detail() {
    // No id to load (e.g. the URL carried none): make sure a stale block cannot persist.
    if (editingItem.id === undefined) {
      setDetailLoading(false);
      return;
    }
    setAttributeDetail(null);
    setLoading({ status: "Loading", message: "Loading Attribute Details" });
    // Blocks further list selection until this settles -- see AttrList's row onClick. Without it,
    // clicking through a pending load stacks fetches for different attributes and the last one to
    // resolve wins, leaving the pane showing something other than the highlighted row.
    setDetailLoading(true);
    if (editingItem.id === undefined) {
      return;
    }
    let res = await ServiceWrapper.doGet(
      `${api_base_url}industry/${industryId}/attribute/list/${editingItem.id}`,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      setAttrListApiCompleted(true);
      setLoading({ status: "Success", message: undefined });
      setAttributeDetail(res.data.data[0]);
      setDetailLoading(false);
    } else {
      setAttrListApiCompleted(true);
      setLoading({ status: "Error", message: "Error while loading details" });
      setDetailLoading(false);
    }
  }

  /* Accepts the detail shape too: the confirm Dialog passes AttributeDetail,
     as the BootBox callback array did (only `.id` is read). */
  async function handle_delete_Attribute(attribute: TAttribute | TAttributeDetail) {
    setShowLoader((prev) => !prev);
    setDeleteInfo({
      status: "Loading",
      message: "Deleting selected Attribute",
    });

    let res = await ServiceWrapper.doGet(
      api_base_url + "attribute/delete/" + attribute.id,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      if (res.data.done) {
        setShowLoader((prev) => !prev);
        setNewAttributeId("");
        toast.success(res.data.message);
        if (
          editingItem !== null &&
          editingItem !== undefined &&
          editingItem.id === attribute.id
        ) {
          setEditingItem(null);
        }
        setAttributeDeleted(true);
        setListReloadCounter((n: any) => n + 1);
      } else {
        setShowLoader((prev) => !prev);
        setDeleteInfo({
          status: "Error",
          message: "Deletion of Attribute Failed",
        });
        setDeleteFailure(res.data.message);
      }
    } else {
      setShowLoader((prev) => !prev);
      setDeleteInfo({
        status: "Error",
        message: "Error while deleting selected Attribute!!",
      });
    }
    setTimeout(() => {
      setDeleteInfo({
        status: null,
        message: "",
      });
    }, 5000);
  }

  return (
    <>
      {/* Delete confirm (was BootBoxManager.confirm with an empty title):
          same legacy copy in the body, same callback — handle_delete_Attribute(AttributeDetail). */}
      <Dialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title="Delete attribute?"
        tone="danger"
        data-testid="attribute-delete-confirm-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              data-testid="attribute-delete-cancel-btn"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger-primary"
              data-testid="attribute-delete-confirm-btn"
              onClick={() => {
                setDeleteConfirmOpen(false);
                if (AttributeDetail) handle_delete_Attribute(AttributeDetail);
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          Do you really want to delete <strong>{AttributeDetail?.name}</strong>?
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink-secondary">
          This cannot be undone.
        </Typography>
      </Dialog>
      {/* Delete-failure notice (was a bootbox acknowledge dialog). */}
      <Dialog
        isOpen={deleteFailure !== null}
        onClose={() => setDeleteFailure(null)}
        title="Attribute Deletion Failure"
        tone="danger"
        showCloseButton={false}
        data-testid="attribute-delete-failure-dialog"
        footer={
          <div className="tw-flex tw-w-full tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="primary"
              data-testid="attribute-delete-failure-ok-btn"
              onClick={() => setDeleteFailure(null)}
            >
              OK, I got it
            </Button>
          </div>
        }
      >
        <Typography variant="body-primary" className="tw-text-ink">
          We are Sorry, We could not delete the selected Attribute Below is more
          information.
        </Typography>
        <Typography variant="body-primary" className="tw-mt-2 tw-text-ink">
          <strong>{deleteFailure}</strong>
        </Typography>
      </Dialog>
      <div>
        <div className="pb-2 d-flex justify-content-between align-items-center tw-border-b tw-border-edge">
          <div className="ms-4">
            <Typography variant="display" as="h1" className="tw-mb-0 tw-text-ink">
              Attributes
            </Typography>
          </div>
          <div className="me-2">
            <Button
              variant="secondary"
              data-testid="attribute-details-btn"
              onClick={() => {
                if (appData.pm.checkPermission("gmsattributes_update")) {
                  if (
                    editingItem === null ||
                    editingItem.form === "view"
                  ) {
                    setEditingItem({
                      form: "edit",
                      id: "0",
                    });
                  } else {
                    setEditingItem(null);
                    setTimeout(() => {
                      setEditingItem({
                        form: "edit",
                        id: "0",
                      });
                    }, 1000);
                  }
                } else {
                  /* toast.success for a denial is the legacy tone, preserved on
                     purpose (same quirk as the category module's Create menu) —
                     same place, until a product decision. */
                  toast.success(
                    "You don't have permission to create the attribute"
                  );
                }
              }}
            >
              Create
            </Button>
          </div>
        </div>
      </div>
      {showLoader && (
        <div className="d-flex h-50 justify-content-center align-items-center">
          <div className="tw-flex tw-justify-center tw-py-10">
            <Spinner size="lg" label="Loading attribute" showLabel data-testid="attribute-details-loading" />
          </div>
        </div>
      )}
      <LoadingSuccessErrorAlert loading={loading} data-testid="attribute-details-status" />

      {loading.status === "Success" && AttributeDetail && (
        <div className="px-3 py-3 mt-0" data-testid="attribute-detail-body">
          {/* py-3, not pb-3: the card needs breathing room below the page header
              (user-reported 2026-09-01). No `cat` here either: styles.css scopes
              `.cat h2`/`.cat p` to 1.4rem/0.8rem BOLD, which outranks the
              Typography tokens (user-reported the oversized heavy type). */}
          <div className="col col-md-12">
            <Card>
              <div className="d-flex justify-content-between ps-1">
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                  <Typography variant="heading-2" as="h2" className="tw-mb-0 tw-text-ink">
                    {AttributeDetail.name}
                  </Typography>
                  {/* AB#14524: tag glyph to the right of the name, tooltip "Custom attribute". */}
                  {AttributeDetail.is_custom === true && (
                    <CustomAttributeTag size={16} data-testid="attribute-details-custom-tag" />
                  )}
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  <Button
                    variant="subtle"
                    size="sm"
                    data-testid="attribute-details-duplicate-btn"
                    onClick={() => {
                      if (appData.pm.checkPermission("gmsattributes_update")) {
                        setEditingItem({
                          form: "duplicate",
                          id: AttributeDetail?.id,
                        });
                      } else {
                        toast.info(
                          "You don't have permission to duplicate the attribute"
                        );
                      }
                    }}
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="attribute-details-edit-btn"
                    onClick={() => {
                      if (
                        appData.pm.checkPermission("gmsattributes_update")
                      ) {
                        setEditingItem({
                          form: "edit",
                          id: AttributeDetail?.id,
                        });
                      } else {
                        toast.info(
                          "You don't have permission to edit the attribute"
                        );
                      }
                    }}
                  >
                    Edit
                  </Button>
                  <div ref={kebabRef} className="tw-relative">
                    <button
                      type="button"
                      id="btnGroupDrop1"
                      data-testid="componets-attributedetails-i-1"
                      aria-haspopup="menu"
                      aria-expanded={kebabOpen}
                      aria-label="More actions"
                      onClick={() => setKebabOpen((open) => !open)}
                      className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-ink-secondary hover:tw-text-ink hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
                    >
                      <MoreVertical size={16} aria-hidden="true" />
                    </button>
                    {kebabOpen && (
                      <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-z-50 tw-w-36">
                        <Menu
                          variant="compact"
                          data-testid="attribute-overflow-menu"
                          items={[
                            {
                              id: "delete",
                              label: "Delete",
                              tone: "danger",
                              "data-testid": "attribute-delete-menu-item",
                              onClick: () => {
                                setKebabOpen(false);
                                if (
                                  appData.pm.checkPermission(
                                    "gmsattributes_delete"
                                  )
                                ) {
                                  setDeleteConfirmOpen(true);
                                } else {
                                  toast.info(
                                    "You don't have permission to delete the attribute"
                                  );
                                }
                              },
                            },
                            {
                              id: "share-url",
                              label: "Share Url",
                              "data-testid": "attribute-share-url-menu-item",
                              onClick: () => {
                                setKebabOpen(false);
                                handle_url_clipbord(AttributeDetail?.id);
                              },
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ps-1">
                <Typography variant="body-primary" as="p" className="mb-2 tw-text-ink">
                  {AttributeDetail.description}
                </Typography>
                <p className="tw-flex tw-items-center tw-gap-2 mt-1 tw-mb-0">
                  <Typography variant="title" as="span" className="tw-text-ink">
                    UID:
                  </Typography>
                  <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                    {AttributeDetail.id || ""}
                  </Typography>
                  <button
                    type="button"
                    data-testid="componets-attributedetails-i-2"
                    aria-label="Copy UID"
                    onClick={() => handle_clipbord(AttributeDetail?.id)}
                    className={`tw-bg-transparent tw-border-0 tw-p-1 tw-rounded-ctl tw-text-accent hover:tw-bg-surface-hover tw-transition-colors ${FOCUS_RING}`}
                  >
                    <Copy size={14} aria-hidden="true" />
                  </button>
                  {clipText !== null && (
                    <Typography variant="caption-metadata" as="span" className="tw-text-tone-success-ink">
                      UID Copied
                    </Typography>
                  )}
                </p>
              </div>
            </Card>
            <br />
            <CategoriesView />
            <br />
            <AttributeValuesView />
            <br />
            <ABSView title={ABS_SELECTION} id={'Abs'} />
            <br />
            <AttributeConstraintView />
            <br />
            {/* <ABSView title={CF_SELECTION} id={'Cf'} /> */}
            {/* <br /> */}
          </div>
        </div>
      )}
    </>
  );
};



export default AttributeDetails
